import React from 'react';
import { opacityToAlpha, ptToCanvas } from '../../../../../../../../slide/infrastructure/module/independent/unit';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { getColorRGB } from '../../../../../../../../slide/infrastructure/module/independent/color/transform';

interface ICoordinates {
    x1: string; y1: string; x2: string; y2: string
}

interface ITransformOrigin {
    'transform-origin': CSSStyleDeclaration['transformOrigin'];
}

interface ITransform {
    transform: CSSStyleDeclaration['transform']
    'transform-origin'?: CSSStyleDeclaration['transformOrigin'];
}

export default class EffectFilter {
    private effectMarkId: string;
    public constructor(effectMarkId: string) {
        this.effectMarkId = effectMarkId;
    }

    /**
     * 代表着每个形状、图片等元素拥有的标志性id
     */
    public getEffectsMarkId = (): string => this.effectMarkId;

    /**
     * 定义外阴影过滤器, 要在<def></def>内使用
     * @param {object} shadow 外阴影的属性集合
     * @param {number} index 第index的元素
     * @param {number} width 元素的宽
     * @param {number} height 元素的高
     */
    public defineOuterShadowFilter = (
        shadow: { dist?: number; dir?: number; blurRad?: number; size?: number; color?: string; opacity?: number },
        index: number,
        width = 1,
        height = 1,
    ): {
        shadowScale: number;
        shadowOffsetX: number;
        shadowOffsetY: number;
        shadowFilter: JSX.Element;
    } => {
        const { dist = 0, dir = 0, blurRad = 0, size = 0, color = '#000000', opacity = 0 } = shadow;
        const theta = dir && dir * Math.PI / 180;
        const dx = dist && ptToCanvas(dist * Math.cos(theta));
        const dy = dist && ptToCanvas(dist * Math.sin(theta));
        const scale = size / 100;
        const [R, G, B] = getColorRGB(color);

        const alpha = opacityToAlpha(opacity);
        const [, , x, y, w, h] = this.calculateShadowFilterBoundary(width, height, dx, dy, blurRad, scale);
        /**
         * 需要改变的
         *      1. shadowfilter: width 和 height 的百分比
         *      2. RGB 调整阴影的颜色
         *      3. A 调整阴影的透明度
         *      4. stdDeviation 调整阴影的模糊度
         *      5. <use> 中的Transform 最后两位 调整阴影的位置（不放大）
         *      6. <use> 中的Transform 第一位和第四位 调整阴影大小（不改变位置，采用基于中心点放大）
         */
        const id = this.getEffectsMarkId();
        const filterId = `outer-shadow-filter-${id}-${index}`;

        const shadowFilter = (
            <filter id={filterId} filterUnits="userSpaceOnUse" x={x} y={y} width={w} height={h}>
                <feGaussianBlur in="SourceAlpha" stdDeviation={blurRad} result="blur"></feGaussianBlur>
                <feComponentTransfer colorInterpolationFilters="sRGB">
                    <feFuncR type="linear" slope="0" intercept={R / 256}></feFuncR>
                    <feFuncG type="linear" slope="0" intercept={G / 256}></feFuncG>
                    <feFuncB type="linear" slope="0" intercept={B / 256}></feFuncB>
                    <feFuncA type="linear" slope={alpha} intercept="0"></feFuncA>
                </feComponentTransfer>
            </filter>
        );

        return {
            shadowScale: scale,
            shadowOffsetX: dx,
            shadowOffsetY: dy,
            shadowFilter,
        };
    };
    /**
     * 直接绑定内阴影过滤器id，实现内阴影效果
     * @param {object} shadow 内阴影属性集合
     * @param {number} index 第index的元素
     * @param {number} width 元素的宽
     * @param {number} height 元素的高
     */
    public defineInnerShadowFilter = (
        shadow: { dist?: number; dir?: number; blurRad?: number; color?: string; opacity?: number },
        index: number,
        width = 1,
        height = 1,
    ): {
        shadowScale: number;
        shadowOffsetX: number;
        shadowOffsetY: number;
        shadowFilter: JSX.Element;
    } => {
        const { dist = 0, dir = 0, blurRad = 0, color = '#000000', opacity = 0 } = shadow;
        const theta = (dir - 180) * Math.PI / 180;
        const dx = dist && ptToCanvas(dist * Math.cos(theta));
        const dy = dist && ptToCanvas(dist * Math.sin(theta));
        const [R, G, B] = getColorRGB(color);
        const alpha = opacityToAlpha(opacity);

        const [, , x, y, w, h] = this.calculateShadowFilterBoundary(width, height, dx, dy, blurRad);

        const id = this.getEffectsMarkId();
        const filterId = `inner-shadow-filter-${id}-${index}`;

        const shadowFilter = (
            <filter id={filterId} filterUnits="userSpaceOnUse" x={x} y={y} width={w} height={h}>
                <feComponentTransfer in="SourceAlpha" result="inner-shadow-reversed">
                    <feFuncA type="table" tableValues="1 0" />
                </feComponentTransfer>
                <feGaussianBlur in="inner-shadow-reversed" stdDeviation={blurRad} result="inner-shadow-blurred" />
                <feOffset dx={dx} dy={dy} result="inner-shadow-offset" />
                <feComponentTransfer colorInterpolationFilters="sRGB" result="inner-shadow-colorred">
                    <feFuncR type="linear" slope="0" intercept={R / 256}></feFuncR>
                    <feFuncG type="linear" slope="0" intercept={G / 256}></feFuncG>
                    <feFuncB type="linear" slope="0" intercept={B / 256}></feFuncB>
                    <feFuncA type="linear" slope={alpha} intercept="0"></feFuncA>
                </feComponentTransfer>
                <feComposite in="inner-shadow-colorred" in2="inner-shadow-offset" operator="in" />
                <feComposite in2="SourceAlpha" operator="in" />
                <feMerge>
                    <feMergeNode in="SourceGraphic" />
                    <feMergeNode />
                </feMerge>
            </filter>
        );
        return {
            shadowScale: 1,
            shadowOffsetX: dx,
            shadowOffsetY: dy,
            shadowFilter,
        };
    };

    /**
     * 使用use复制元素，设置阴影
     * @param {number} effectsMarkId 效果选项Id
     * @param {number} elementIndex 元素的Index
     * @param {number} scale 阴影放大比例
     * @param {number} dx x轴偏移距离，右正左负
     * @param {number} dy y轴偏移距离，下正上负
     */
    public setOuterShadow = (
        effectsMarkId: string,
        elementIndex: number,
        scale: number,
        dx: number,
        dy: number,
    ): JSX.Element => (
        <g filter={`url(#outer-shadow-filter-${effectsMarkId}-${elementIndex})`}>
            {/* @ts-ignore */}
            <use href={`#use-shadow-${effectsMarkId}-${elementIndex}`} style={{ transform: `matrix(${scale},0,0,${scale},${dx},${dy})` }} transformOrigin="50% 50%" />
        </g>
    );

    /**
     * 直接绑定映像过滤器id与遮罩id，实现映像效果
     * @param {object} reflection 映像属性集合
     * @param {number} index 第index的元素
     * @param {number} width 元素的宽
     * @param {number} height 元素的高
     * @param {number} shadowOffsetX 阴影x轴偏移距离，因为也要为元素的阴影实现镜像效果
     * @param {number} shadowOffsetY 阴影y轴偏移距离
     */
    public defineReflectionFilter = (
        reflection: { blurRad?: number; dist?: number; endOpacity?: number; size?: number; startOpacity?: number },
        index: number,
        width = 1,
        height = 1,
        shadowOffsetX = 0,
        shadowOffsetY = 0,
        rotatedDeg = 0,
    ): {
        reflectionRotationStyle: ITransform | null;
        reflectionFilter: JSX.Element;
    } => {
        const { blurRad = 0, dist = 0, endOpacity = 99.7, size = 0, startOpacity = 0 } = reflection;
        const id = this.getEffectsMarkId();
        const filterId = `reflection-filter-${id}-${index}`;
        const maskId = `reflection-mask-${id}-${index}`;

        const {
            filterStartX, filterStartY, filterWidth, filterHeight,
            maskStartX, maskStartY, maskWidth, maskHeight,
            maskRectX, maskRectY, maskRectWidth, maskRectHeight,
            boundingCoordinates, maskRotationStyle, reflectionRotationStyle,
        } = this.calculateReflectionFilterBoundary(
            width, height, dist, blurRad,
            shadowOffsetX, shadowOffsetY, rotatedDeg,
        );

        const reflectionFilter = (
            <React.Fragment>
                {
                    !browser.isSafari && <filter id={filterId} filterUnits="userSpaceOnUse" x={`${filterStartX}%`} y={`${filterStartY}%`} width={`${filterWidth}%`} height={`${filterHeight}%`}>
                        <feGaussianBlur in="SourceGraph" stdDeviation={blurRad} result="blur"></feGaussianBlur>
                    </filter>
                }

                <mask id={maskId} x={maskStartX} y={maskStartY} width={maskWidth} height={maskHeight} maskUnits="userSpaceOnUse">

                    <linearGradient id={`${maskId}-linear-gradient`} gradientUnits="objectBoundingBox" {...boundingCoordinates}>
                        <stop offset="50%" stopColor="#ffffff" stopOpacity={opacityToAlpha(startOpacity)}></stop>
                        <stop offset={`${50 + (size / 2)}%`} stopColor="#ffffff" stopOpacity={opacityToAlpha(endOpacity)}></stop>
                    </linearGradient>

                    {size && <rect x={maskRectX} y={maskRectY} width={maskRectWidth} height={maskRectHeight}
                        fill={`url(#${maskId}-linear-gradient)`} transform={`rotate(${-2 * rotatedDeg})`} {...maskRotationStyle}></rect>}
                </mask>
            </React.Fragment>
        );

        return {
            reflectionRotationStyle,
            reflectionFilter,
        };
    };

    /**
     * 使用use复制元素，设置映像
     * @param {number} effectsMarkId 效果选项Id
     * @param {number} elementIndex 元素的Index
     * @param {number} offsetY 映像向y轴正方向偏移距离
     */
    public setReflection = (
        effectsMarkId: string,
        elementIndex: number,
        reflectionRotationStyle: ITransform,
    ): JSX.Element => {
        const safariAttr = browser.isSafari || browser.isWxMiniProgram
            ? { mask: `url(#reflection-mask-${effectsMarkId}-${elementIndex})` }
            : { filter: `url(#reflection-filter-${effectsMarkId}-${elementIndex})`, mask: `url(#reflection-mask-${effectsMarkId}-${elementIndex})` };
        const template = <g {...safariAttr}>
            <use href={`#use-reflection-${effectsMarkId}-${elementIndex}`} {...reflectionRotationStyle} pointerEvents="none" />
        </g>;
        return template;
    };

    /**
     * 直接绑定柔化遮罩id，实现柔化效果
     * @param {object} softEdge 边缘柔化属性集合
     * @param {string} path svg路径
     * @param {number} index 第index的元素
     * @param {number} scaleX svg在X方向缩放比例
     * @param {number} scaleY svg在Y方向缩放比例
     */
    public defineSoftEdgeFilter = (
        softEdge: { size?: number },
        path: string,
        index: number,
        scaleX: number,
        scaleY: number,
    ): {
        softEdgeFilter: JSX.Element;
    } => {
        const { size = 0 } = softEdge;
        const blur = size ? (0.1 * size) + 9.9 : 0;
        const trueWidthScale = (scaleX || 1) * (1 - (size / 100));
        const trueHeightScale = (scaleY || 1) * (1 - (size / 100));

        const isCustomPath = !!scaleX || !!scaleY;
        const transformOrigin = isCustomPath ? null : { 'transform-origin': '50% 50%' }; /** 自定义形状柔化会有问题 */
        const id = this.getEffectsMarkId();
        const filterId = `soft-edge-filter-${id}-${index}`;
        const maskId = `soft-edge-mask-${id}-${index}`; /** 柔化元素 需要绑定这个id */

        const softEdgeFilter = (
            <React.Fragment>
                <filter id={filterId} filterUnits="userSpaceOnUse">
                    <feGaussianBlur stdDeviation={blur} in="SourceGraphic"></feGaussianBlur>
                </filter>
                <mask id={maskId}>
                    <path d={path}
                        fill="white"
                        opacity="1"
                        filter={`url(#${filterId})`}
                        style={{ transform: `scale(${trueWidthScale}, ${trueHeightScale})` }}
                        {...transformOrigin}></path>
                </mask>
            </React.Fragment>
        );

        return { softEdgeFilter };
    };

    /**
     * 直接绑定发光过滤器id，设置发光效果
     * @param {object} glow 发光效果属性集合
     * @param {number} index 第index的元素
     * @param {number} width 元素的宽
     * @param {number} height 元素的高
     */
    public defineGlowFilter = (
        glow: { size?: number; color?: string; opacity?: number },
        index: number,
        width = 1,
        height = 1,
    ): {
        glowFilter: JSX.Element;
    } => {
        const { size = 0, color = '#000000', opacity = 0 } = glow;
        const [R, G, B] = getColorRGB(color);
        const alpha = opacityToAlpha(opacity);
        const [
            rad, blur, xInFilter, yInFilter,
            widthInFilter, heightInFilter,
        ] = this.calculateGlowFilterBoundary(width, height, size);
        const id = this.getEffectsMarkId();
        const filterId = `glow-filter-${id}-${index}`;
        const glowFilter = (
            <filter id={filterId} filterUnits="userSpaceOnUse" x={xInFilter} y={yInFilter} width={widthInFilter} height={heightInFilter}>
                <feMorphology operator="dilate" radius={(rad as number) / 3} in="SourceAlpha" result="glow-thickened" /> {/** 比较耗时，如果rad超过50 */}
                <feGaussianBlur stdDeviation={blur} result="glow-blurred"></feGaussianBlur>
                <feComponentTransfer colorInterpolationFilters="sRGB" result="glow-colorred">
                    <feFuncR type="linear" slope="0" intercept={R / 256}></feFuncR>
                    <feFuncG type="linear" slope="0" intercept={G / 256}></feFuncG>
                    <feFuncB type="linear" slope="0" intercept={B / 256}></feFuncB>
                    <feFuncA type="linear" slope={alpha} intercept="0"></feFuncA>
                </feComponentTransfer>
            </filter>
        );
        return { glowFilter };
    };
    /**
     * 使用use复制元素，实现发光效果
     * @param {number} effectsMarkId 效果选项Id
     * @param {number} elementIndex 元素的Index
     */
    public setGlow = (
        effectsMardId: string,
        elementIndex: number,
    ): JSX.Element => (
        <g filter={`url(#glow-filter-${effectsMardId}-${elementIndex})`} >
            <use href={`#use-glow-${effectsMardId}-${elementIndex}`} />
        </g>
    );

    /**
     * 计算阴影过滤器的边界
     * @param {number} width svg 宽度
     * @param {number} height svg 高度
     * @param {number} dx x轴偏移，右正坐负
     * @param {number} dy y轴偏移，下正上负数
     * @param {number} blurRad 虚化半径。指阴影整体的虚化半径，包括发光
     * @param {number} scale 扩大比例，最大是2，最小是0。指阴影本身的放大比例，不包含发光时的阴影
     * @returns {string[]} x, y, filterWidth, filterHeight的百分比
     */
    private calculateShadowFilterBoundary = (
        width: number,
        height: number,
        dx: number,
        dy: number,
        blurRad: number,
        scale = 1,
    ): string[] => {
        let x = -50;
        let y = -50;
        let filterWidth = 200;
        let filterHeight = 200;

        const w = Math.round(width) || 1;
        const h = Math.round(height) || 1;
        const scaleX = scale;
        const scaleY = scale;

        const displacementXpercent = Math.abs(dx) / w * 100;
        const displacementYpercent = Math.abs(dy) / h * 100;

        x = x - displacementXpercent - (2 * blurRad);
        y = y - displacementYpercent - (2 * blurRad);

        filterWidth = filterWidth + (2 * displacementXpercent) + (2 * blurRad) + (scaleX * 100);
        filterHeight = filterHeight + (2 * displacementYpercent) + (2 * blurRad) + (scaleY * 100);

        return [`${scaleX}`, `${scaleY}`, `${x}%`, `${y}%`, `${filterWidth}%`, `${filterHeight}%`];
    };
    /**
     * 计算发光过滤器的边界
     * @param {number} width svg 宽度
     * @param {number} height svg 高度
     * @param {number} size 发光大小值0～150磅
     * @returns {string[]} rad, blur x, y, filterWidth, filterHeight的比例
     */
    private calculateGlowFilterBoundary = (
        width: number,
        height: number,
        size: number,
    ): (string|number)[] => {
        let x = -50;
        let y = -50;
        let filterWidth = 200;
        let filterHeight = 200;
        const rad = size;

        const w = Math.round(width) || 1;
        const h = Math.round(height) || 1;

        const blur = rad === 0 ? 0 : (Math.PI - 1) * Math.log(rad);
        const scaleInWidth = rad / w;
        const scaleInHeight = rad / h;
        x = x - (scaleInWidth * 100) - (2 * size);
        y = y - (scaleInHeight * 100) - (2 * size);
        const scaleX = 1 + (2 * scaleInWidth);
        const scaleY = 1 + (2 * scaleInHeight);
        filterWidth = filterWidth + (scaleInWidth * 100) + (4 * rad) + (scaleX * 100);
        filterHeight = filterHeight + (scaleInHeight * 100) + (4 * rad) + (scaleY * 100);

        return [
            rad, `${blur}`, `${x}%`, `${y}%`,
            `${filterWidth}%`, `${filterHeight}%`,
        ];
    };

    /**
     * 计算映像过滤器的边界（含旋转）
     * @param {*} width svg 宽度
     * @param {*} height svg 高度
     * @param {*} dist 映像与实体间的距离
     * @param {*} blurRad 映像模糊度
     * @param {*} shadowOffsetX 如有阴影存在，其阴影与实体在x轴方向上的距离
     * @param {*} shadowOffsetY 如有阴影存在，其阴影与实体在y轴方向上的距离
     * @param {*} rotatedDeg 实体旋转的顺时针方向上的角度，0～359度
     */
    // eslint-disable-next-line complexity
    private calculateReflectionFilterBoundary = (
        width: number,
        height: number,
        dist: number,
        blurRad: number,
        shadowOffsetX = 0,
        shadowOffsetY = 0,
        rotatedDeg = 0,
    ): {
        filterStartX: number;
        filterStartY: number;
        filterWidth: number;
        filterHeight: number;
        maskStartX: number;
        maskStartY: number;
        maskWidth: number;
        maskHeight: number;
        maskRectX: number;
        maskRectY: number;
        maskRectWidth: number;
        maskRectHeight: number;
        boundingCoordinates: ICoordinates | null;
        maskRotationStyle: ITransformOrigin | null;
        reflectionRotationStyle: ITransform | null;
    } => {
        const w = Math.round(width) || 1;
        const h = Math.round(height) || 1;
        const dy = ptToCanvas(dist);
        const shadowDx = Math.abs(shadowOffsetX);
        const shadowDy = Math.abs(shadowOffsetY);
        let filterStartX = -100 - (2 * blurRad / w * 100) - (2 * shadowDx / w * 100)
      + (Math.abs(Math.sin(rotatedDeg * Math.PI / 180)) * 100);
        let filterStartY = -100 - (2 * blurRad / h * 100) - (2 * shadowDy / h * 100)
      + (Math.abs(Math.cos(rotatedDeg * Math.PI / 180)) * 100);
        const filterWidth = 300 + (4 * blurRad / w * 100) + (4 * shadowDx / w * 100)
      + (Math.abs(Math.cos(rotatedDeg * Math.PI / 180)) * 100);
        const filterHeight = 300 + (4 * dy / h * 100) + (4 * blurRad / h * 100) + (4 * shadowDy / h * 100)
      + (Math.abs(Math.sin(rotatedDeg * Math.PI / 180)) * 100);

        const transDxAfterRotation = - dy * Math.sin(rotatedDeg * Math.PI / 180);
        const transDyAfterRotation = - dy * Math.cos(rotatedDeg * Math.PI / 180);

        const positiveDy = shadowOffsetY > 0;

        let boundingCoordinates: ICoordinates | null = null;
        let maskRotationStyle: ITransformOrigin | null = null;
        let reflectionRotationStyle: ITransform | null = null;

        let maskStartX = -(4 * shadowDx) - 100 - (2 * blurRad);
        let maskRectX = maskStartX;

        let maskStartY = positiveDy ? -50 - (2 * shadowOffsetY) : 0;
        let maskRectY = maskStartY;

        const maskWidth = 2 * ((2 * w) + (4 * shadowDx) + 150 + (4 * blurRad));
        const maskRectWidth = maskWidth;

        let maskHeight = (2 * h) + dy + (positiveDy ? (2 * shadowDy) + 50 : 0);
        const maskRectHeight = maskHeight;

        if (rotatedDeg === 0) {
            boundingCoordinates = { x1: '0', y1: '0', x2: '0', y2: '1' };
            maskRotationStyle = { 'transform-origin': '100% 100%' };
            reflectionRotationStyle = { transform: `matrix(1,0,0,-1,0,${(2 * h) + dy})` };
        } else if (rotatedDeg < 45) {
            boundingCoordinates = { x1: '0', y1: '0', x2: '0', y2: '1' };// 坐标有问题，连线不垂直与水平线
            maskStartY += 0.5 * Math.sin(rotatedDeg * Math.PI / 180) * maskHeight;
            maskHeight += maskStartY;
            maskRotationStyle = { 'transform-origin': '100% 100%' };
            reflectionRotationStyle = { transform: `scale(1 -1) rotate(${2 * rotatedDeg}) translate(${transDxAfterRotation} ${transDyAfterRotation})`, ...maskRotationStyle };
        } else if (rotatedDeg < 90) {
            boundingCoordinates = { x1: '0', y1: '0', x2: '0', y2: '1' };// 坐标有问题，连线不垂直与水平线
            maskStartY += 0.5 * Math.cos(rotatedDeg * Math.PI / 180) * maskHeight;
            maskHeight += maskStartY;
            maskRotationStyle = { 'transform-origin': '100% 100%' };
            reflectionRotationStyle = { transform: `scale(1 -1) rotate(${2 * rotatedDeg}) translate(${transDxAfterRotation} ${transDyAfterRotation})`, ...maskRotationStyle };
        } else if (rotatedDeg === 90) {
            boundingCoordinates = { x1: '1', y1: '0', x2: '0', y2: '0' };
            maskStartX -= 0.5 * maskHeight;
            maskRectX -= 0.5 * maskHeight;
            maskRotationStyle = { 'transform-origin': '100% 100%' };
            reflectionRotationStyle = { transform: `scale(1 -1) rotate(${2 * rotatedDeg}) translate(${transDxAfterRotation} ${transDyAfterRotation})`, ...maskRotationStyle };
        } else if (rotatedDeg <= 180) {
            boundingCoordinates = { x1: '0', y1: '1', x2: '0', y2: '0' };
            filterStartY -= 200;
            maskStartY -= (0.5 * maskHeight * (1 - Math.cos(rotatedDeg * Math.PI / 180)));
            maskRectY -= 0.5 * maskHeight;
            maskRotationStyle = { 'transform-origin': '100% 0%' };
            reflectionRotationStyle = { transform: `scale(1 -1) rotate(${2 * rotatedDeg}) translate(${transDxAfterRotation} ${transDyAfterRotation})`, ...maskRotationStyle };
        } else if (rotatedDeg < 270) {
            filterStartX -= 200;
            filterStartY -= 200;
            maskStartX -= 2 * maskHeight * Math.sin((rotatedDeg - 180) * Math.PI / 180);
            maskStartY -= (0.5 * maskHeight * (1 - Math.cos(rotatedDeg * Math.PI / 180)));
            maskRectY -= ((0.5 * maskHeight) + (2 * blurRad) + dy);
            maskRotationStyle = { 'transform-origin': '0% 0%' };
            reflectionRotationStyle = { transform: `scale(1 -1) rotate(${2 * rotatedDeg}) translate(${transDxAfterRotation} ${transDyAfterRotation})`, ...maskRotationStyle };
            boundingCoordinates = { x1: '0', y1: '1', x2: '0', y2: '0' };
        } else if (rotatedDeg === 270) {
            filterStartX -= 200;
            filterStartY -= 200;
            maskStartX -= 2 * maskHeight * Math.sin((rotatedDeg - 180) * Math.PI / 180);
            maskStartY -= (0.5 * maskHeight * (1 - Math.cos(rotatedDeg * Math.PI / 180)));
            maskRectX -= ((0.5 * maskWidth) + (2 * blurRad) + dy);
            maskRectY -= ((0.5 * maskHeight) + (2 * blurRad));
            maskRotationStyle = { 'transform-origin': '0% 0%' };
            reflectionRotationStyle = { transform: `scale(1 -1) rotate(${2 * rotatedDeg}) translate(${transDxAfterRotation} ${transDyAfterRotation})`, ...maskRotationStyle };
            boundingCoordinates = { x1: '0', y1: '0', x2: '1', y2: '0' };
        } else if (rotatedDeg < 360) {
            filterStartX -= 200;
            maskStartX -= 2 * maskHeight * Math.sin((360 - rotatedDeg) * Math.PI / 180);
            maskRectX = maskStartX;
            maskStartY += Math.min(0.5 * maskHeight, (maskHeight * (1 + Math.sin((rotatedDeg * Math.PI / 180)))));
            maskRotationStyle = { 'transform-origin': '0% 100%' };
            reflectionRotationStyle = { transform: `scale(1 -1) rotate(${2 * rotatedDeg}) translate(${transDxAfterRotation} ${transDyAfterRotation})`, ...maskRotationStyle };
            boundingCoordinates = { x1: '0', y1: '0', x2: '0', y2: '1' };
        }
        return {
            filterStartX, filterStartY, filterWidth, filterHeight,
            maskStartX, maskStartY, maskWidth, maskHeight,
            maskRectX, maskRectY, maskRectWidth, maskRectHeight,
            boundingCoordinates, maskRotationStyle, reflectionRotationStyle,
        };
    };
}
